import { type UnitView } from "@aybuben/core/content/course.ts";
import type { LessonDef, UnitDef } from "@aybuben/core/content/types.ts";
import { KIND_RULES } from "@aybuben/core/lessons.ts";
import {
	finished,
	lessonAvailable,
	nextLesson,
	started,
	unitDue,
} from "@aybuben/core/progress/query.ts";
import type { GameState } from "@aybuben/core/state.ts";

import type { UiKey } from "../i18n/en.ts";
import { stopsOf, stopState, type Stop, type StopState } from "../places/stops.ts";

export type Translate = (key: UiKey, params?: Record<string, string | number>) => string;

/** A request to scroll the map to the learner's place. `n` tells one request from the next. */
export interface FocusRequest {
	n: number;
	smooth: boolean;
	/** Set when the request is for a unit's header (a place sheet's row), not the learner's place. */
	unitId?: string;
}

/**
 * The note shown for a locked unit. `prev` is undefined only for a corrupt
 * save (`unlocked: {}` with unit 1 itself locked) — degrade to a plain
 * "Locked." rather than crash. Exported so this branch is testable without a DOM.
 */
export function lockedNote(prev: UnitView | undefined, t: Translate): string {
	return prev ? t("home.lockedAfter", { n: prev.number }) : t("home.locked");
}

/**
 * How many columns to lay `n` chips out in.
 *
 * Five is the widest row that fits five chips plus gaps on a 390px phone, and
 * was hardcoded back when every unit taught exactly five letters. It no longer
 * does: unit 12 teaches eight, and D's copula unit teaches six forms. Filling
 * rows to five leaves those as 5+3 and 5+1; spreading them over the fewest
 * rows that will hold them gives 4+4 and 3+3 instead.
 *
 * Exported for home.test.ts, which pins that every unit in the course gets a
 * full last row.
 */
export function chipColumns(n: number): number {
	const rows = Math.ceil(n / 5);
	return rows === 0 ? 1 : Math.ceil(n / rows);
}

/**
 * Runs of consecutive units that share a section, in course order: one divider
 * each. Consecutive rather than grouped, so a section can never be drawn out of
 * course order (the validator also keeps a section's units together). Units of
 * a release without sections form one run, drawn without a divider.
 */
export function sectionRuns<T extends { section: { id: string } | null }>(
	units: readonly T[],
): T[][] {
	const runs: T[][] = [];
	for (const u of units) {
		const last = runs.at(-1);
		// `last` is non-empty: a run is only ever created holding a unit.
		if (last && last[0]!.section?.id === u.section?.id) last.push(u);
		else runs.push([u]);
	}
	return runs;
}

/**
 * Where the map lands when no lesson is current (every open lesson played):
 * the last lesson of the furthest unlocked unit. Undefined only for a corrupt
 * save with nothing unlocked.
 */
export function lastOpenLesson(st: GameState, units: readonly UnitDef[]): string | undefined {
	return units.findLast((u) => !!st.unlocked[u.id])?.lessons.at(-1)?.id;
}

/** The learner's place on the map: the current lesson, else the furthest open one. Home and AppLayout must agree on it. */
export function placeOf(st: GameState, units: readonly UnitDef[]): string | undefined {
	return nextLesson(st, units)?.lesson.id ?? lastOpenLesson(st, units);
}

/**
 * Whether the unit's "current" label reads "keep going" rather than "start
 * here". Keyed off the unit, not off a lesson's `times`, which is always 0 for
 * the current lesson: `nextLesson` only ever selects a lesson with no
 * completions, so reading the node's own count made "keep going" unreachable
 * and its two catalog strings dead.
 */
export function unitStarted(st: GameState, u: UnitView): boolean {
	return started(st, u.def);
}

export type LessonNodeState = "locked" | "offline" | "done" | "open";

export interface LessonNodeModel {
	state: LessonNodeState;
	open: boolean;
	/** Open but unplayable: offline, with this lesson's sounds not downloaded. */
	blocked: boolean;
	times: number;
	ariaKey: UiKey;
}

export function lessonNode(
	st: GameState,
	u: UnitView,
	lesson: LessonDef,
	offline: boolean,
): LessonNodeModel {
	const open = !!st.unlocked[u.id] && lessonAvailable(st, u.def, lesson.id);
	const blocked = open && offline;
	const times = st.done[lesson.id] ?? 0;
	// Locked before done: a lesson can hold both states at once, and which one
	// it is stops the learner tapping matters more than how often they've
	// played it.
	if (!open) return { state: "locked", open, blocked, times, ariaKey: "home.lessonAriaLocked" };
	if (blocked) return { state: "offline", open, blocked, times, ariaKey: "home.lessonAriaOffline" };
	if (times > 0) return { state: "done", open, blocked, times, ariaKey: "home.lessonAriaDone" };
	return { state: "open", open, blocked, times, ariaKey: "home.lessonAria" };
}

export interface UnitNote {
	key: UiKey;
	params?: Record<string, string | number>;
}

export interface UnitHeaderModel {
	unlocked: boolean;
	due: ReturnType<typeof unitDue>;
	/** `finished()`, not memory: memory fades by design, so a crown driven by it would appear after a passed checkpoint and then vanish. */
	complete: boolean;
	note: UnitNote | null;
}

export function unitHeader(
	st: GameState,
	u: UnitView,
	prev: UnitView | undefined,
	next: UnitView | undefined,
): UnitHeaderModel {
	const unlocked = !!st.unlocked[u.id];
	// The hint is about what opens next, so it goes once that is open.
	const nextOpen = next ? !!st.unlocked[next.id] : finished(u.def, st);
	let note: UnitNote | null = null;
	if (!unlocked)
		note = prev ? { key: "home.lockedAfter", params: { n: prev.number } } : { key: "home.locked" };
	else if (!nextOpen)
		note = next
			? { key: "home.unlockNext", params: { n: next.number } }
			: { key: "home.unlockFinish" };
	return {
		unlocked,
		due: unitDue(u.def, st),
		complete: unlocked && finished(u.def, st),
		note,
	};
}

export interface HomeLesson {
	lesson: LessonDef;
	/** 1-based position in the unit: the kind and unit alone repeat within a unit. */
	position: number;
	/** The node that says keep going / start here, and the jump button's target. */
	isCurrent: boolean;
	/** The node the map scrolls to. */
	isPlace: boolean;
	offline: boolean;
}

export interface HomeUnit {
	unit: UnitView;
	/** The unit before/after in course order, not in its section's run, so neighbours mean the same in the locked and unlock notes. */
	prev: UnitView | undefined;
	next: UnitView | undefined;
	lessons: HomeLesson[];
}

export type HomeEntry =
	| { kind: "stop"; stop: Stop; state: StopState; units: HomeUnit[] }
	| { kind: "unit"; unit: HomeUnit };

export interface HomeSection {
	/** Null for a release without sections: drawn without a divider. */
	section: UnitView["section"];
	units: HomeUnit[];
	/** The section's units grouped into stops; a unit with no stop stands alone. */
	entries: HomeEntry[];
}

export function homeSections(
	units: readonly UnitView[],
	st: GameState,
	isOffline?: (u: UnitView, l: LessonDef) => boolean,
): HomeSection[] {
	const defs = units.map((u) => u.def);
	const currentId = nextLesson(st, defs)?.lesson.id;
	const placeId = placeOf(st, defs);
	const stopOf = new Map<string, Stop>();
	for (const s of stopsOf(units)) for (const u of s.units) stopOf.set(u.id, s);
	return sectionRuns(units).map((run) => {
		const homeUnits: HomeUnit[] = run.map((unit) => {
			const idx = units.findIndex((x) => x.id === unit.id);
			return {
				unit,
				prev: units[idx - 1],
				next: units[idx + 1],
				lessons: unit.def.lessons.map((lesson, i) => ({
					lesson,
					position: i + 1,
					isCurrent: currentId === lesson.id,
					isPlace: placeId === lesson.id,
					offline: isOffline?.(unit, lesson) ?? false,
				})),
			};
		});
		const entries: HomeEntry[] = [];
		for (const hu of homeUnits) {
			const stop = stopOf.get(hu.unit.id);
			const last = entries.at(-1);
			if (stop && last?.kind === "stop" && last.stop.id === stop.id) last.units.push(hu);
			else if (stop) entries.push({ kind: "stop", stop, state: stopState(st, stop), units: [hu] });
			else entries.push({ kind: "unit", unit: hu });
		}
		// `run` is never empty: sectionRuns only creates runs holding a unit.
		return { section: run[0]!.section, units: homeUnits, entries };
	});
}

/**
 * What a lesson's sheet says: why it can or cannot be played, then the pass
 * mark of a graded kind. `open` and `blocked` as in lessonNode; `units` is the
 * whole course, to name the unit before a locked one.
 */
export function lessonSheetNote(
	st: GameState,
	units: readonly UnitView[],
	u: UnitView,
	lesson: LessonDef,
	offline: boolean,
	t: Translate,
): { open: boolean; blocked: boolean; note: string } {
	const unlockedUnit = !!st.unlocked[u.id];
	const open = unlockedUnit && lessonAvailable(st, u.def, lesson.id);
	const blocked = open && offline;
	const times = st.done[lesson.id] ?? 0;
	const reason = blocked
		? t("home.lessonOffline")
		: open
			? times > 0
				? times === 1
					? t("home.lessonDoneOnceNote")
					: t("home.lessonDoneNote", { n: times })
				: t("home.lessonFirstNote")
			: unlockedUnit
				? t("home.lessonLocked")
				: lockedNote(units[units.findIndex((x) => x.id === u.id) - 1], t);
	const { passMark } = KIND_RULES[lesson.kind];
	const note =
		passMark === undefined
			? reason
			: `${reason} ${t("home.passMarkNote", { pct: Math.round(passMark * 100) })}`;
	return { open, blocked, note };
}
